/*
  이 파일은 FastAPI 서버와 실시간 양방향 통신을 위한 WebSocket 클라이언트 모듈입니다.
  회의 중 발생하는 음성 데이터, 번역된 자막, RAG 결과 등을 WebSocket을 통해 송수신하며,
  사용자 연결, 메시지 전송, 자동 재연결, 이벤트 리스닝 등 다양한 기능을 제공합니다.
  이 클래스는 싱글톤으로 설계되어 Vue 컴포넌트 등 어디에서든 import 하여 동일한 인스턴스를 공유하며 사용할 수 있습니다.
*/

import { webSocketApiV1BaseURL } from '@/api/apiConfig'; // apiConfig.js 에서 webSocketApiV1BaseURL import

class FastApiWebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = null;
    this.eventListeners = {
      'rag_result': [],
      'subtitle': [], // 자막 이벤트
      'speaker_changed': [],    // 발언자 변경 시 발생
      'speak_request_denied': [] // 발언권 요청 거절 시 발생
    };
  }

  /**
   * WebSocket 연결을 초기화하고 사용자 정보를 전송합니다.
   */
  connect(meetingId, userId, userName, sourceLanguage) {
    if (this.ws) this.disconnect(); // 기존 연결 종료

    // apiConfig.js에서 가져온 webSocketApiV1BaseURL 사용
    const fastApiUrl = `${webSocketApiV1BaseURL}/api/v1/ws-fast-api/${sourceLanguage}/${meetingId}`;
    console.log('[WebSocket] 연결 시도:', fastApiUrl);

    this.ws = new WebSocket(fastApiUrl);

    this.ws.onopen = () => {
      console.log('[WebSocket] 연결 성공');
      this._registerUser(meetingId, userId, userName, sourceLanguage);
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[WebSocket] Message received:', data);

        // --- 제어 메시지 우선 처리 ---
        // 서버로부터 오는 발언권 관련 제어 메시지를 먼저 확인합니다.
        if (data.type) {
          switch (data.type) {
            case 'speaker_changed':
              // 발언자가 변경되었다는 이벤트를 발생시켜 컴포넌트에 알립니다.
              this.emitEvent('speaker_changed', data);
              return;
            case 'speak_request_denied':
              // 발언권 요청이 거절되었다는 이벤트를 발생시킵니다.
              this.emitEvent('speak_request_denied', data);
              return;
          }
        }
        
        // --- 기존 데이터 메시지 처리 ---
        // 새로운 data_source 필드를 기준으로 메시지 처리
        switch (data.data_source) {
          case 'task_a_translation':
            // Task A: 사용자 발화 번역 (자막 및/또는 TTS)
            console.log('[WebSocket] Handling Task A (User Translation) data:', data);

            // ⏱️ E2E 지연 시간 계산 (API 요청 시작부터 WebSocket 메시지 수신까지)
            if (window.lastTranslationRequestStartTime) {
              const e2eLatency = performance.now() - window.lastTranslationRequestStartTime;
              console.log(`⏱️ [E2E 지연시간] 번역 API 호출부터 WebSocket 수신까지: ${e2eLatency.toFixed(2)}ms (${(e2eLatency / 1000).toFixed(2)}초)`);
              console.log(`⏱️ [성능 측정] 발화 → STT → 번역 → TTS → 브로드캐스트 → 수신 전체 프로세스 완료`);
              // 측정 완료 후 초기화
              window.lastTranslationRequestStartTime = null;
            }

            // 자막 처리
            if (data.translated_content_details &&
                data.translated_content_details.user_utterance &&
                data.translated_content_details.user_utterance.text) {
              const subtitleText = data.translated_content_details.user_utterance.text;
              const speakerName = data.speaker_name || data.userName || data.speaker || 'Unknown Speaker';
              const speakerId = data.user_id || data.userId || '';

              this.emitEvent('subtitle', {
                text: subtitleText,
                speaker_name: speakerName,
                user_id: speakerId
              });
            }

            // TTS 오디오 처리 (클라이언트가 현재 오디오를 기대하는 상태인지에 따라 재생 여부 결정)
            // 예를 들어, 클라이언트의 translation_option 설정 등을 확인하여 처리
            if (data.translated_content_details && data.translated_content_details.tts_audio_base64) {
              // 기존 handleTranslationResponse 함수가 base64 오디오 데이터를 처리한다고 가정
              this.handleTranslationResponse(data.translated_content_details.tts_audio_base64);
            }
            break;

          case 'task_b_rag':
            // Task B: RAG 검색 결과
            console.log('[WebSocket] Handling Task B (RAG Result) data:', data);

            if (data.rag_result) { // RAG 결과 객체가 존재하고 null이 아닐 때만 처리
              this.handleRagResult(data.rag_result);
            } else {
              // data_source가 'task_b_rag'이지만 rag_result가 null인 경우는
              // 백엔드에서 RAG 결과를 생성하려 했으나 실패한 경우일 수 있습니다.
              // 이 경우, UI에 아무것도 표시하지 않거나, 최소한의 알림을 줄 수 있습니다.
              // 요구사항에 따라 RAG 결과가 없으면 표현되지 않아야 하므로, 이 부분은 주로 로깅용입니다.
              console.log('[WebSocket] RAG result expected (task_b_rag) but rag_result field is null.');
            }
            break;

          case 'task_b_rag_skipped':
            // Task B: RAG 검색 스킵 또는 결과 없음
            // data.rag_result는 이 경우 백엔드에서 null로 설정되어 옵니다.
            console.log('[WebSocket] Handling Task B (RAG Skipped/No Result) data:', data);
            // 요구사항: RAG 결과가 없으면 표현되지 않아야 하므로, UI 변경 없음.
            // 필요시 내부적으로 로깅하거나 사용자에게 매우 간결한 피드백(예: "추가 정보 없음")을 줄 수도 있습니다.
            // if (data.original_stt_details) {
            //   console.log('[WebSocket] RAG skipped for STT:', data.original_stt_details.text);
            // }
            break;

          default:
            console.log('[WebSocket] 기타 메시지 수신 (알 수 없는 data_source 값 또는 data_source 없음):', data);
            // 기존 data.type을 사용하는 로직을 여기에 fallback으로 넣을 수도 있으나,
            // data_source 기반으로 완전히 전환하는 것이 좋습니다.
        }
      } catch (err) {
        console.error('[WebSocket] 메시지 처리 오류:', err);
      }
    };

    this.ws.onerror = (err) => {
      console.error('[WebSocket] 연결 오류:', err);
    };

    this.ws.onclose = (event) => {
      console.log('[WebSocket] 연결 종료:', event.code, event.reason);

      if (event.code !== 1000) {
        console.log('[WebSocket] 비정상 종료로 재연결 시도 예정');
        clearTimeout(this.reconnectInterval);
        this.reconnectInterval = setTimeout(() => {
          this.connect(meetingId, userId, userName, sourceLanguage);
        }, 5000);
      }
    };

    window.fastApiWs = this.ws; // 디버깅 또는 수동 접근용
    return this.ws;
  }

  /**
   * 서버로 특정 타입과 데이터를 갖는 메시지를 전송하는 표준 메소드.
   * @param {string} type - 메시지 타입 (e.g., 'request_to_speak', 'finished_speaking')
   * @param {object} payload - 함께 전송할 데이터 객체
   */
  send(type, payload = {}) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const message = {
        type: type,
        ...payload,
        timestamp: new Date().toISOString()
      };
      this.ws.send(JSON.stringify(message));
      console.log(`[WebSocket] 메시지 전송: ${type}`, message);
    } else {
      console.warn(`[WebSocket] 연결 상태가 아니므로 '${type}' 메시지 전송 불가`);
    }
  }

  /**
   * 서버로 텍스트 또는 명령 메시지를 전송합니다.
   */
  sendMessage(messageData, userId, meetingId) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        ...messageData,
        userId,
        meetingId,
        timestamp: new Date().toISOString()
      }));
    } else {
      console.warn('[WebSocket] 연결 상태가 아니므로 메시지 전송 불가');
    }
  }

  /**
   * 이벤트 리스너 등록
   */
  addEventListener(event, callback) {
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(callback);
  }

  /**
   * 등록된 이벤트 실행
   */
  emitEvent(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(cb => cb(data));
    }
  }

  /**
   * base64 오디오 데이터를 디코딩하여 재생
   */
  handleTranslationResponse(base64Audio) {
    const binaryString = atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    audioContext.decodeAudioData(bytes.buffer)
      .then(buffer => {
        const source = audioContext.createBufferSource();
        source.buffer = buffer;

        // --- 볼륨 조절 로직 추가 ---
        // 1. GainNode (볼륨 조절기) 생성
        const gainNode = audioContext.createGain();

        // 2. 볼륨 설정 (예: 2배로 증폭)
        // 이 값을 조절하여 원하는 볼륨을 설정할 수 있습니다.
        gainNode.gain.value = 2.5; 

        // 3. 연결 순서 변경: source -> gainNode -> destination
        source.connect(gainNode);

        source.connect(audioContext.destination);
        source.start(0);
        console.log('[오디오] 번역 음성 재생 시작(볼륨 2배)');
      })
      .catch(err => {
        console.error('[오디오] 재생 오류:', err);
      });
  }

  /**
   * RAG 결과 수신 시 이벤트 발생
   */
  handleRagResult(ragResult) {
    console.log('[WebSocket] RAG 결과 수신');
    this.emitEvent('rag_result', ragResult);
  }

  /**
   * WebSocket 연결 종료
   */
  disconnect() {
    if (this.ws) {
      this.ws.onclose = null; // 재연결 로직 방지
      this.ws.close(1000, 'Normal disconnection');
      this.ws = null;
      console.log('[WebSocket] 연결이 정상적으로 종료되었습니다.');
    }
    // 2025.06.18 양권상: 연결 종료 시 모든 리스너를 정리하여 메모리 누수를 방지합니다.
    this.eventListeners = {
      'rag_result': [],
      'subtitle': [],
      'speaker_changed': [],
      'speak_request_denied': []
    };
    clearTimeout(this.reconnectInterval);
  }

  /**
   * 사용자 연결 정보를 새로운 프로토콜에 맞게 서버로 전송합니다.
   * 메소드 이름을 _registerUser로 변경하고, payload 구조를 사용합니다.
   */
  _registerUser(meetingId, userId, userName, sourceLanguage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const payload = {
        type: 'register_user', // type을 'register_user'로 변경
        payload: {             // BE 요구사항에 따라 사용자 정보를 payload 객체에 포함
          meeting_id: meetingId,
          user_id: userId,
          user_name: userName,
          source_language: sourceLanguage,
        },
        timestamp: new Date().toISOString()
      };
      console.log('[WebSocket] 사용자 등록 정보 전송:', payload);
      this.ws.send(JSON.stringify(payload));
    } else {
      console.error('[WebSocket] 연결이 열려 있지 않아 사용자 등록 실패');
    }
  }

  // 2025.06.18 양권상: 컴포넌트 unmount 시 이벤트 리스너 메모리 누수 방지를 위해 추가
  removeEventListener(event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(
        (cb) => cb !== callback
      );
    }
  }
}

// 싱글톤 인스턴스 내보내기
const fastApiWebSocket = new FastApiWebSocketService();
export default fastApiWebSocket;